/**
 * League Super Squares scoring (frontend mirror of backend/db/leagues/leagueSuperSquares.js).
 * Keep in sync with the backend helper.
 *
 *   return = round((base + streakBonus) × accuracyMult × wicketMult × maidenMult)
 *
 * Accuracy ×4–×6 at 4–6 correct (3 correct is not a multiplier); streak chips
 * 10/25/50/100/200 for 2–6 in a row (added before multipliers); any wicket doubles
 * once (capped at ×2); a completed maiden halves the total.
 * Same-ball bonuses merge into one combo celebration — they still all apply.
 */

import { computeBallWon, displayBallResult } from "../../Squares/shared/squaresBallResults.js";

/** Streak chip bonus by longest consecutive correct streak length. */
export const STREAK_BONUS_BY_LENGTH = Object.freeze({
    2: 10,
    3: 25,
    4: 50,
    5: 100,
    6: 200,
});

export type SuperSquaresBreakdown = {
    baseReturn: number;
    wicketCount: number;
    correctCount: number;
    streakLength: number;
    streakBonus: number;
    wicketMult: number;
    accuracyMult: number;
    maidenMult: number;
    /** Multiplicative part (wicket × accuracy × maiden) — streak is added to base before multiply. */
    combinedMult: number;
    returnCoins: number;
};

export type SuperSquaresBonusEvent =
    | {
          type: "wicket";
          ballIndex: number;
          wicketCount: number;
          wicketMult: number;
      }
    | {
          type: "accuracy";
          ballIndex: number;
          correctCount: number;
          accuracyMult: number;
      }
    | {
          type: "streak";
          ballIndex: number;
          streakLength: number;
          streakBonus: number;
      }
    | {
          type: "maiden";
          ballIndex: number;
          maidenMult: number;
      };

/**
 * Several bonuses landing at once (or a viewer joining mid-over) — one celebration
 * showing every active bonus instead of queuing a stamp per event.
 */
export type SuperSquaresComboEvent = {
    type: "combo";
    ballIndex: number;
    wicketCount: number;
    wicketMult: number;
    correctCount: number;
    accuracyMult: number;
    streakLength: number;
    streakBonus: number;
    maidenMult: number;
    combinedMult: number;
};

export type SuperSquaresCelebration = SuperSquaresBonusEvent | SuperSquaresComboEvent;

export function superSquaresEventKey(event: SuperSquaresCelebration): string {
    if (event.type === "wicket") return `${event.type}-${event.ballIndex}-${event.wicketMult}`;
    if (event.type === "accuracy") return `${event.type}-${event.ballIndex}-${event.accuracyMult}`;
    if (event.type === "streak") return `${event.type}-${event.ballIndex}-${event.streakBonus}`;
    if (event.type === "maiden") return `${event.type}-${event.ballIndex}-${event.maidenMult}`;
    return `${event.type}-${event.ballIndex}-${event.combinedMult}-${event.streakBonus}-${event.maidenMult}`;
}

export function superSquaresComboEvent(
    breakdown: SuperSquaresBreakdown,
    ballIndex: number,
): SuperSquaresComboEvent {
    return {
        type: "combo",
        ballIndex,
        wicketCount: breakdown.wicketCount,
        wicketMult: breakdown.wicketMult,
        correctCount: breakdown.correctCount,
        accuracyMult: breakdown.accuracyMult,
        streakLength: breakdown.streakLength,
        streakBonus: breakdown.streakBonus,
        maidenMult: breakdown.maidenMult,
        combinedMult: breakdown.combinedMult,
    };
}

/** Stamp label for the multiplicative boost (includes maiden ×0.5). */
export function superSquaresBoostLabel(combinedMult: number, streakBonus: number): string {
    const n = Number(combinedMult);
    if (Number.isFinite(n) && n !== 1) return `×${n}`;
    return `+${Math.max(0, Math.round(Number(streakBonus) || 0))}`;
}

type Selection = { run?: string; runLabel?: string; chips?: number; odds?: string | number };

/** True when a counted ball result is a dismissal (W / W+N). */
export function isWicketBallResult(raw: unknown): boolean {
    if (raw == null || String(raw).trim() === "") return false;
    const s = String(raw).trim().toLowerCase();
    if (s === "w" || s === "wicket" || s === "wkt") return true;
    if (/^w\+[1-5]$/i.test(s)) return true;
    if (/^wicket\+[1-5]$/i.test(s) || /^wkt\+[1-5]$/i.test(s)) return true;
    // Display-normalised "W"
    if (displayBallResult(raw) === "W") return true;
    return false;
}

export function countWicketsInBallResults(ballResults: unknown[] | null | undefined): number {
    if (!Array.isArray(ballResults)) return 0;
    let n = 0;
    for (const raw of ballResults.slice(0, 6)) {
        if (isWicketBallResult(raw)) n += 1;
    }
    return n;
}

function ballIsCorrect(
    selections: Record<string, Selection> | null | undefined,
    ballResults: unknown[],
    index: number,
): boolean {
    const sel = selections?.[String(index + 1)];
    const pred = sel?.run ?? sel?.runLabel ?? "";
    if (!pred) return false;
    const raw = ballResults[index];
    const display = displayBallResult(raw) ?? String(raw ?? "");
    return computeBallWon(display, String(pred)) === true;
}

export function countCorrectBalls(
    selections: Record<string, Selection> | null | undefined,
    ballResults: unknown[] | null | undefined,
): number {
    if (!Array.isArray(ballResults) || ballResults.length === 0) return 0;
    let n = 0;
    const limit = Math.min(ballResults.length, 6);
    for (let i = 0; i < limit; i += 1) {
        if (ballIsCorrect(selections, ballResults, i)) n += 1;
    }
    return n;
}

/** Longest run of consecutive correct balls (in delivery order). */
export function longestCorrectStreak(
    selections: Record<string, Selection> | null | undefined,
    ballResults: unknown[] | null | undefined,
): number {
    if (!Array.isArray(ballResults) || ballResults.length === 0) return 0;
    let best = 0;
    let run = 0;
    const limit = Math.min(ballResults.length, 6);
    for (let i = 0; i < limit; i += 1) {
        if (ballIsCorrect(selections, ballResults, i)) {
            run += 1;
            if (run > best) best = run;
        } else {
            run = 0;
        }
    }
    return best;
}

/** Flat chip bonus for a streak length (0 if shorter than 2). */
export function streakBonusForLength(streakLength: number): number {
    const n = Math.max(0, Math.floor(Number(streakLength) || 0));
    if (n >= 6) return STREAK_BONUS_BY_LENGTH[6];
    if (n >= 5) return STREAK_BONUS_BY_LENGTH[5];
    if (n >= 4) return STREAK_BONUS_BY_LENGTH[4];
    if (n >= 3) return STREAK_BONUS_BY_LENGTH[3];
    if (n >= 2) return STREAK_BONUS_BY_LENGTH[2];
    return 0;
}

/** ×2 if any wicket fell; extra wickets do not stack. */
export function wicketMultiplier(wicketCount: number): number {
    const n = Math.max(0, Math.floor(Number(wicketCount) || 0));
    return n >= 1 ? 2 : 1;
}

/** ×4/×5/×6 at 4–6 correct; else ×1. */
export function accuracyMultiplier(correctCount: number): number {
    const n = Math.max(0, Math.floor(Number(correctCount) || 0));
    if (n >= 6) return 6;
    if (n >= 5) return 5;
    if (n >= 4) return 4;
    return 1;
}

/** True when six counted balls are all dots or plain wickets (no runs). */
export function isMaidenOver(ballResults: unknown[] | null | undefined): boolean {
    if (!Array.isArray(ballResults) || ballResults.length < 6) return false;
    for (const raw of ballResults.slice(0, 6)) {
        const d = displayBallResult(raw);
        if (d !== "0" && d !== "W") return false;
    }
    return true;
}

/** ×0.5 on a completed maiden over; else ×1. */
export function maidenMultiplier(ballResults: unknown[] | null | undefined): number {
    return isMaidenOver(ballResults) ? 0.5 : 1;
}

/** (base + streakBonus) × accuracyMult × wicketMult × maidenMult */
export function superSquaresReturnCoins(
    baseReturn: number,
    accuracyMult: number,
    streakBonus: number,
    wicketMult: number,
    maidenMult = 1,
): number {
    const base = Math.max(0, Number(baseReturn) || 0);
    const acc = Math.max(1, Number(accuracyMult) || 1);
    const streak = Math.max(0, Math.round(Number(streakBonus) || 0));
    const wickets = Math.max(1, Number(wicketMult) || 1);
    const maiden = Number(maidenMult);
    const maidenFactor = Number.isFinite(maiden) && maiden > 0 ? maiden : 1;
    const summed = base + streak;
    if (summed <= 0) return 0;
    return Math.round(summed * acc * wickets * maidenFactor);
}

function baseReturnFromSelections(
    selections: Record<string, Selection> | null | undefined,
    ballResults: unknown[],
): number {
    let sum = 0;
    const limit = Math.min(ballResults.length, 6);
    for (let i = 0; i < limit; i += 1) {
        const sel = selections?.[String(i + 1)];
        const pred = sel?.run ?? sel?.runLabel ?? "";
        if (!pred || !sel) continue;
        if (!ballIsCorrect(selections, ballResults, i)) continue;
        const chips = Number(sel.chips) || 0;
        const odds = Math.max(0, parseFloat(String(sel.odds ?? "")) || 0);
        if (chips <= 0 || odds <= 0) continue;
        sum += Math.round(chips * odds * 100) / 100;
    }
    return Math.round(sum * 100) / 100;
}

export function computeSuperSquaresBreakdown(
    selections: Record<string, Selection> | null | undefined,
    ballResultsPartial: unknown[] | null | undefined,
): SuperSquaresBreakdown {
    const balls = Array.isArray(ballResultsPartial) ? ballResultsPartial.slice(0, 6) : [];
    const baseReturn = Math.max(0, baseReturnFromSelections(selections, balls));
    const wicketCount = countWicketsInBallResults(balls);
    const correctCount = countCorrectBalls(selections, balls);
    const streakLength = longestCorrectStreak(selections, balls);
    const streakBonus = streakBonusForLength(streakLength);
    const wicketMult = wicketMultiplier(wicketCount);
    const accuracyMult = accuracyMultiplier(correctCount);
    const maidenMult = maidenMultiplier(balls);
    const combinedMult = wicketMult * accuracyMult * maidenMult;
    return {
        baseReturn,
        wicketCount,
        correctCount,
        streakLength,
        streakBonus,
        wicketMult,
        accuracyMult,
        maidenMult,
        combinedMult,
        returnCoins: superSquaresReturnCoins(baseReturn, accuracyMult, streakBonus, wicketMult, maidenMult),
    };
}

/** One ball of the over as the celebration draws it. */
export type SuperSquaresBallCell = {
    /** Display result ("4", "W"), null while not bowled. */
    result: string | null;
    pick: string | null;
    /** null = no pick or no result yet. */
    correct: boolean | null;
    wicket: boolean;
};

function isPendingBallResult(raw: unknown): boolean {
    const s = String(raw ?? "").trim();
    return s === "" || s === "..." || s === "–" || s === "-";
}

/** Six cells for the over; balls after `uptoIndex` render as not bowled. */
export function superSquaresBallCells(
    selections: Record<string, Selection> | null | undefined,
    ballResults: unknown[] | null | undefined,
    uptoIndex = 5,
): SuperSquaresBallCell[] {
    const balls = Array.isArray(ballResults) ? ballResults : [];
    return Array.from({ length: 6 }, (_, i) => {
        const sel = selections?.[String(i + 1)];
        const pick = String(sel?.runLabel ?? sel?.run ?? "").trim() || null;
        const raw = i <= uptoIndex ? balls[i] : undefined;
        if (isPendingBallResult(raw)) return { result: null, pick, correct: null, wicket: false };
        const rawText = String(raw).trim();
        const result = displayBallResult(rawText) ?? rawText;
        return {
            result,
            pick,
            correct: pick ? ballIsCorrect(selections, balls, i) : null,
            wicket: isWicketBallResult(raw),
        };
    });
}

/** Indices of the longest correct run (latest one on ties). */
export function superSquaresStreakIndices(cells: SuperSquaresBallCell[]): number[] {
    let best: number[] = [];
    let run: number[] = [];
    cells.forEach((cell, i) => {
        if (cell.correct === true) {
            run = [...run, i];
            if (run.length >= best.length) best = run;
        } else {
            run = [];
        }
    });
    return best.length >= 2 ? best : [];
}

/** Diff events when a new ball is appended (for live overlay). */
export function superSquaresEventsForNewBall(
    prevBalls: unknown[],
    nextBalls: unknown[],
    selections: Record<string, Selection> | null | undefined,
): SuperSquaresBonusEvent[] {
    if (!Array.isArray(nextBalls) || nextBalls.length <= (prevBalls?.length ?? 0)) return [];
    const events: SuperSquaresBonusEvent[] = [];
    for (let i = prevBalls.length; i < nextBalls.length; i += 1) {
        const slice = nextBalls.slice(0, i + 1);
        const prevSlice = nextBalls.slice(0, i);
        const raw = nextBalls[i];
        const prevWicketMult = wicketMultiplier(countWicketsInBallResults(prevSlice));
        const nextWicketCount = countWicketsInBallResults(slice);
        const nextWicketMult = wicketMultiplier(nextWicketCount);
        if (isWicketBallResult(raw) && nextWicketMult > prevWicketMult) {
            events.push({
                type: "wicket",
                ballIndex: i,
                wicketCount: nextWicketCount,
                wicketMult: nextWicketMult,
            });
        }
        const prevCorrect = countCorrectBalls(selections, prevSlice);
        const nextCorrect = countCorrectBalls(selections, slice);
        const prevAcc = accuracyMultiplier(prevCorrect);
        const nextAcc = accuracyMultiplier(nextCorrect);
        if (nextAcc > prevAcc && nextAcc >= 4) {
            events.push({
                type: "accuracy",
                ballIndex: i,
                correctCount: nextCorrect,
                accuracyMult: nextAcc,
            });
        }
        const prevStreak = streakBonusForLength(longestCorrectStreak(selections, prevSlice));
        const nextStreakLen = longestCorrectStreak(selections, slice);
        const nextStreak = streakBonusForLength(nextStreakLen);
        if (nextStreak > prevStreak) {
            events.push({
                type: "streak",
                ballIndex: i,
                streakLength: nextStreakLen,
                streakBonus: nextStreak,
            });
        }
        const prevMaiden = maidenMultiplier(prevSlice);
        const nextMaiden = maidenMultiplier(slice);
        if (nextMaiden < prevMaiden) {
            events.push({
                type: "maiden",
                ballIndex: i,
                maidenMult: nextMaiden,
            });
        }
    }
    return events;
}

/**
 * One delivery can fire several bonuses (wicket + accuracy tier + longer streak + maiden).
 * Catch-up can also dump several unseen bonuses at once. Either way: one stamp,
 * walking every active bonus, then the stacked total.
 *
 * Formula those stamps are celebrating:
 *   round((Σ chips×odds on correct balls + streakBonus) × accuracyMult × wicketMult × maidenMult)
 */
export function mergeSuperSquaresEvents(
    events: SuperSquaresBonusEvent[],
    breakdown: SuperSquaresBreakdown,
): SuperSquaresCelebration | null {
    if (events.length === 0) return null;
    if (events.length === 1) return events[0]!;
    return superSquaresComboEvent(breakdown, events[events.length - 1]!.ballIndex);
}

/**
 * Live sequence for an over: one celebration per ball that newly earned a bonus.
 * Same-ball bonuses merge (see {@link mergeSuperSquaresEvents}).
 */
export function superSquaresCelebrationsForOver(
    selections: Record<string, Selection> | null | undefined,
    ballResults: unknown[] | null | undefined,
): SuperSquaresCelebration[] {
    const results = Array.isArray(ballResults) ? ballResults.slice(0, 6) : [];
    const out: SuperSquaresCelebration[] = [];
    for (let i = 0; i < results.length; i += 1) {
        const prev = results.slice(0, i);
        const next = results.slice(0, i + 1);
        const events = superSquaresEventsForNewBall(prev, next, selections);
        const celebration = mergeSuperSquaresEvents(
            events,
            computeSuperSquaresBreakdown(selections, next),
        );
        if (celebration) out.push(celebration);
    }
    return out;
}
